// src/components/editor/StoryElementDetails.jsx
//
// The block that opens beneath a selected row: what the element is, and every
// relationship it has. This is the ticket's "clicking Actor 1 shows Chapter 1
// and the Shop scene beneath it", plus the place those relationships are added,
// relabelled and removed.
//
// It renders for chapters too, with one difference: a chapter has no stored
// name or description to edit here, because a chapter IS a heading in the
// manuscript. Renaming it belongs to the outline row's own rename, and its text
// belongs to the document. So a chapter gets relationships only.
//
// Edits autosave on a debounce rather than behind a Save button: the panel is
// 280px of sidebar beside a manuscript, and an author who clicks back into the
// text with unsaved changes in a sidebar has lost them. Debounced writes mean
// there is nothing to lose.
//
// NOTE: the panel container ships global CSS that forces any MUI Button or
// IconButton to full-width gold, so every control here is a plain Box.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { connectTargets, relationshipsFor } from '../../utils/storyElements';

const GOLD = '#F5D76E';
const SAVE_DEBOUNCE_MS = 450;

const inputSx = {
  width: '100%',
  font: 'inherit',
  fontSize: 12,
  color: 'white',
  backgroundColor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '4px',
  padding: '7px 8px',
  '&:focus': { outline: 'none', borderColor: GOLD },
};

const labelSx = {
  display: 'block',
  color: GOLD,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  mb: 0.5,
};

export default function StoryElementDetails({
  subject,            // describeRef() result for the selected row
  board,              // { nodes, edges }
  chapters,           // flattenOutline() result
  error,              // a write that failed, reported here rather than panel-top
  onPatchNode,   // immediate, local only — keeps the row label in step as you type
  onSaveNode,    // debounced, writes to the server
  onRemoveNode,
  onAddRelationship,
  onRelabelRelationship,
  onRemoveRelationship,
}) {
  const isNode = subject?.kind === 'node';
  const node = isNode
    ? (board.nodes || []).find((n) => String(n._id) === subject.nodeId)
    : null;

  const [name, setName] = useState(node?.name || '');
  const [desc, setDesc] = useState(node?.desc || '');
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const saveTimer = useRef(null);
  const savedTimer = useRef(null);
  const pending = useRef({});
  // Which element `pending` belongs to, so a write still waiting when these
  // details close can be sent rather than dropped.
  const pendingNodeId = useRef(null);
  // The latest onSaveNode, for the unmount flush, which only sees the first.
  const onSaveNodeRef = useRef(onSaveNode);
  onSaveNodeRef.current = onSaveNode;

  // Re-seed when the selection moves to a different element. Without this the
  // inputs would keep showing the previous element's text.
  useEffect(() => {
    setName(node?.name || '');
    setDesc(node?.desc || '');
    setAdding(false);
    setTarget('');
    setNewLabel('');
  }, [subject?.ref]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing the details — selecting another row unmounts them — sends a write
  // that was still waiting out the debounce. Clearing it instead lost any edit
  // made in the last moments before the click, while the board and the
  // editor's hover card went on showing it as if it had been saved.
  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    clearTimeout(savedTimer.current);
    const payload = pending.current;
    pending.current = {};
    if (pendingNodeId.current && Object.keys(payload).length) {
      onSaveNodeRef.current?.(pendingNodeId.current, payload);
    }
  }, []);

  const flashSaved = () => {
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 1200);
  };

  /**
   * Debounced write, accumulating every field edited during the wait.
   *
   * The fields must accumulate rather than replace: typing a name and then
   * clicking into the description within the debounce window used to cancel
   * the pending name write and send only the description. Local state already
   * showed the new name, so the loss was invisible until a reload.
   *
   * The node id is captured at queue time, not read when the timer fires — by
   * then the author may have selected something else, and the write belongs to
   * the element they were actually editing.
   */
  const queueSave = (fields) => {
    const nodeId = subject.nodeId;
    pending.current = { ...pending.current, ...fields };
    pendingNodeId.current = nodeId;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = pending.current;
      pending.current = {};
      const ok = await onSaveNode?.(nodeId, payload);
      if (ok !== false) flashSaved();
    }, SAVE_DEBOUNCE_MS);
  };

  const relationships = useMemo(
    () => relationshipsFor(subject?.ref, {
      edges: board.edges || [], nodes: board.nodes || [], chapters,
    }),
    [subject?.ref, board.edges, board.nodes, chapters],
  );

  const targets = useMemo(
    () => connectTargets({
      nodes: board.nodes || [], chapters, excludeRef: subject?.ref,
    }),
    [board.nodes, chapters, subject?.ref],
  );

  const submitRelationship = async () => {
    if (!target) return;
    await onAddRelationship?.({
      from: subject.ref, to: target, label: newLabel.trim() || 'connected to',
    });
    setAdding(false);
    setTarget('');
    setNewLabel('');
  };

  return (
    <Box sx={{ px: 2, py: 1.5, backgroundColor: 'rgba(0,0,0,0.16)' }}>
      {/* Beside the control that failed, not at the top of the panel. The panel
          body scrolls, and an author who has scrolled down to their actor never
          saw a message painted above the Chapters section — which is how a
          relationship that refused to delete read as a dead button. role=alert
          so it is announced rather than only painted. */}
      {error && (
        <Typography
          role="alert"
          sx={{
            mb: 1.25, px: 1, py: 0.75, borderRadius: '4px',
            fontSize: 11, lineHeight: 1.5,
            color: '#F3C0B4', backgroundColor: 'rgba(200,80,60,0.18)',
          }}
        >
          {error}
        </Typography>
      )}

      {isNode && (
        <>
          <Box sx={{ mb: 1.25 }}>
            <Typography component="label" sx={labelSx}>Name</Typography>
            <Box
              component="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                // An empty name is refused by the API, so don't send one —
                // the author is mid-edit, not asking to clear it.
                if (e.target.value.trim()) {
                  onPatchNode?.(subject.nodeId, { name: e.target.value });
                  queueSave({ name: e.target.value });
                }
              }}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ mb: 1.25 }}>
            <Typography component="label" sx={labelSx}>Description</Typography>
            <Box
              component="textarea"
              rows={3}
              value={desc}
              onChange={(e) => {
                setDesc(e.target.value);
                // Patched locally like the name, so the editor's hover card and
                // a later re-selection see it before any refetch.
                onPatchNode?.(subject.nodeId, { desc: e.target.value });
                queueSave({ desc: e.target.value });
              }}
              sx={{ ...inputSx, resize: 'vertical', minHeight: 54 }}
            />
          </Box>
        </>
      )}

      <Typography component="h4" sx={{ ...labelSx, mb: 0.75 }}>
        Relationships ({relationships.length})
      </Typography>

      {relationships.length === 0 ? (
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11.5, lineHeight: 1.6, mb: 1 }}>
          Not connected to anything yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
          {relationships.map(({ edge, outgoing, other }) => (
            <Box
              key={edge._id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                '&:hover .rv-rel-remove': { opacity: 1 },
              }}
            >
              <Box
                component="input"
                aria-label="Relationship"
                defaultValue={edge.label || 'connected to'}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== edge.label) onRelabelRelationship?.(edge._id, next);
                }}
                sx={{ ...inputSx, width: 96, flexShrink: 0, fontSize: 11 }}
              />
              <Box sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, flexShrink: 0 }}>
                {outgoing ? '→' : '←'}
              </Box>
              <Box
                title={other.missing ? 'This no longer exists in the manuscript' : other.name}
                sx={{
                  flex: 1, minWidth: 0, fontSize: 12,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: other.missing ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.85)',
                  fontStyle: other.missing ? 'italic' : 'normal',
                }}
              >
                {other.missing ? 'unplaced' : other.name}
              </Box>
              <Box
                component="button"
                type="button"
                className="rv-rel-remove"
                aria-label={`Remove relationship to ${other.name}`}
                onClick={() => onRemoveRelationship?.(edge._id)}
                sx={{
                  display: 'inline-flex', border: 'none', background: 'transparent',
                  color: 'rgba(255,255,255,0.45)', cursor: 'pointer', p: 0.25,
                  borderRadius: '3px', opacity: 0, transition: 'opacity .15s ease',
                  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' },
                  '&:focus-visible': { opacity: 1, outline: `2px solid ${GOLD}` },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {adding ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
          <Box
            component="select"
            aria-label="Connect to"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            sx={{ ...inputSx, '& option': { color: 'black' } }}
          >
            <option value="">Choose what to connect to…</option>
            {targets.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Box>
          <Box
            component="input"
            aria-label="Relationship"
            placeholder="e.g. appears in, rivals"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            sx={inputSx}
          />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Box
              component="button"
              type="button"
              onClick={submitRelationship}
              disabled={!target}
              sx={{
                font: 'inherit', fontSize: 11, fontWeight: 600, px: 1.25, py: 0.5,
                borderRadius: '4px', border: `1px solid ${GOLD}`, cursor: 'pointer',
                color: GOLD, backgroundColor: 'transparent',
                '&:disabled': { opacity: 0.4, cursor: 'default' },
                '&:hover:not(:disabled)': { backgroundColor: 'rgba(245,215,110,0.15)' },
              }}
            >
              Connect
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setAdding(false)}
              sx={{
                font: 'inherit', fontSize: 11, px: 1.25, py: 0.5, borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                color: 'rgba(255,255,255,0.7)', backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => setAdding(true)}
          disabled={targets.length === 0}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.25,
            font: 'inherit', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            color: GOLD, backgroundColor: 'transparent', border: 'none', p: 0, mb: 1,
            '&:disabled': { opacity: 0.4, cursor: 'default' },
            '&:hover:not(:disabled)': { textDecoration: 'underline' },
          }}
        >
          <AddIcon sx={{ fontSize: 13 }} /> Add relationship
        </Box>
      )}

      {isNode && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onRemoveNode?.(subject.nodeId, node?.name)}
            sx={{
              font: 'inherit', fontSize: 10.5, cursor: 'pointer', p: 0,
              border: 'none', background: 'transparent', textDecoration: 'underline',
              color: 'rgba(255,255,255,0.45)',
              '&:hover': { color: '#E88' },
            }}
          >
            Remove {subject.type}
          </Box>
          <Typography sx={{
            fontSize: 9.5, color: GOLD, opacity: saved ? 1 : 0,
            transition: 'opacity .3s ease',
          }}>
            Saved
          </Typography>
        </Box>
      )}
    </Box>
  );
}
